import { inject, Injectable } from '@angular/core';
import { LoggerWrapper } from '../logger/logger-wrapper.service';
import {
  ActiviteEnCours,
  ActiviteEnCoursService,
  ActiviteEnCoursType,
} from '../../components/activite-en-cours/activite-en-cours.service';
import { ChronoActiviteService } from '../chrono-activite/chrono-activite.service';
import { AlertController, LoadingController } from '@ionic/angular';
import { UserService } from '../auth/user.service';
import { TranslateService } from '@ngx-translate/core';
import { ActiviteInterneService } from './activite-interne.service';
import { Router } from '@angular/router';
import { ChronoActivite } from '../chrono-activite/chrono-activite.model';
import { CrActiviteInterne } from '../../models/activite-interne/activite-interne.model';
import dayjs from 'dayjs';

@Injectable({ providedIn: 'root' })
export class DemarrerActiviteInterneService {
  private readonly logger: LoggerWrapper = inject(LoggerWrapper);
  private readonly activiteEnCoursService: ActiviteEnCoursService = inject(ActiviteEnCoursService);
  private readonly chronoActiviteService: ChronoActiviteService = inject(ChronoActiviteService);
  private readonly alertCtrl: AlertController = inject(AlertController);
  private readonly userService: UserService = inject(UserService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly activiteInterneService: ActiviteInterneService = inject(ActiviteInterneService);
  private readonly router: Router = inject(Router);
  private readonly loadingCtrl: LoadingController = inject(LoadingController);

  public async startActiviteInterne(
    type: ActiviteEnCoursType,
    idTempo?: string,
    typeActiviteInternePlanifiee?: string
  ): Promise<boolean> {
    if (await this.checkActiviteStarted()) {
      this.logger.warn(`Activité déjà démarrée`);
      return false;
    }
    const loading = await this.loadingCtrl.create();
    await loading.present();
    try {
      let activiteEnCours = await this.createActiviteEnCours(type, idTempo, typeActiviteInternePlanifiee);
      await this.activiteEnCoursService.demarrerActiviteEncours(activiteEnCours);
      await this.chronoActiviteService.demarrerChronoPourActivite(activiteEnCours.id, activiteEnCours.type);
      return true;
    } catch (error) {
      this.logger.error(error, `Erreur startActiviteInterne`);
      return false;
    } finally {
      await loading.dismiss();
    }
  }

  async stopActiviteInterne(idActiviteInterne: string) {
    const activiteEnCours = await this.activiteEnCoursService.getActiviteEnCours();
    if (activiteEnCours?.id === idActiviteInterne) {
      await this.chronoActiviteService.supprimerChronoPourActivite(idActiviteInterne);
      await this.activiteEnCoursService.supprimerActiviteEnCours(idActiviteInterne);
    }
  }

  async redirectToActiviteInternePage(idActiviteInterne: string) {
    const activiteEnCours = await this.activiteEnCoursService.getActiviteEnCours();
    const chrono = await this.chronoActiviteService.arreterChronoPourActivite(idActiviteInterne);
    if (activiteEnCours?.id === idActiviteInterne) {
      const cr = this.buildCR(activiteEnCours, chrono);
      await this.redirect(cr);
    }
  }

  private async checkActiviteStarted(): Promise<boolean> {
    const activiteEnCours: ActiviteEnCours = await this.activiteEnCoursService.getActiviteEnCours();
    if (activiteEnCours) {
      this.alertCtrl
        .create({
          message: this.translate.instant('GLOBAL.TOO_MANY_ACTIVITES'),
          buttons: [this.translate.instant('GLOBAL.OK')],
        })
        .then((alert) => alert.present());
      return true;
    }
    return false;
  }

  private async createActiviteEnCours(
    type: ActiviteEnCoursType,
    idTempo?: string,
    typeActiviteInternePlanifiee?: string
  ): Promise<ActiviteEnCours> {
    const activiteInterne = {
      id_intervenant: this.userService.getId(),
      id_tempo: idTempo,
      matricule: this.userService.getMatricule(),
      planification_date_debut: null,
      planification_date_fin: null,
      type_activite_interne_planifiee: typeActiviteInternePlanifiee,
    };
    const idActivite: string = await this.activiteInterneService.saveActivite(activiteInterne);

    let libelle: string = '';
    switch (type) {
      case 'renfort':
        libelle = 'Renfort';
        break;
      case 'activite-interne':
        libelle = 'Activité interne';
        break;
      case 'ot':
        break;
    }
    return {
      id: idActivite,
      type: type,
      libelle: libelle,
      id_tempo: idTempo,
    } as ActiviteEnCours;
  }

  private async redirect(cr: any) {
    if (this.isOnEcrtPage()) {
      await this.alert(cr);
      return;
    }
    const activiteEnCours = await this.activiteEnCoursService.getActiviteEnCours();
    if (this.router.url.includes('/mnt/ots/' + activiteEnCours?.id + '/cr')) return;

    await this.goToActiviteInternePage(cr);
  }

  private async goToActiviteInternePage(cr: any) {
    await this.router.navigate(['/mnt/activite-interne'], {
      state: { cr: cr, editable: true },
    });
  }

  private isOnEcrtPage(): boolean {
    return this.router.url === '/iot/iot-capteur-cr-pose';
  }
  private async alert(cr: any) {
    const alert = await this.alertCtrl.create({
      message: this.translate.instant(
        "Vous souhaitez retourner sur l'OT. Les opérations de pose non finalisées vont être perdues, souhaitez vous poursuivre ?"
      ),
      buttons: [
        {
          text: this.translate.instant('Oui'),
          handler: () => {
            this.goToActiviteInternePage(cr);
          },
        },
        { text: this.translate.instant('Non') },
      ],
    });
    await alert.present();
  }

  private buildCR(activiteEnCours: ActiviteEnCours, chrono: ChronoActivite): CrActiviteInterne {
    let duree_intervention = chrono.calculerDureeIntervention();

    return {
      type: 'cr-activite-interne',
      id_activite_interne: activiteEnCours.id,
      cr_commentaire_intervenant: null,
      idTempo: chrono.idActivite,
      estRenfort: activiteEnCours.type === 'renfort',
      cr_type_activite_realisee: activiteEnCours.type_activite_interne,

      cr_activite_interne_date_debut: dayjs(chrono.dateDebutIntervention).format(),
      cr_activite_interne_date_fin: dayjs(chrono.dateFinIntervention).format(),
      cr_activite_interne_duree: duree_intervention,
      cr_activite_interne_duree_in_seconds: duree_intervention * 60,
      cr_deplacement_date_debut: dayjs(chrono.dateDebutIntervention).format(),
      cr_deplacement_date_fin: dayjs(chrono.dateFinIntervention).format(),
      cr_deplacement_duree: 0,
      cr_deplacement_duree_in_seconds: 0,
      cr_duree_totale: duree_intervention,
      cr_duree_totale_in_seconds: duree_intervention * 60,
    };
  }

  async reloadActiviteInterne() {
    const activiteEnCours: ActiviteEnCours = await this.activiteEnCoursService.reloadActivite();
    if (activiteEnCours) {
      await this.chronoActiviteService.reloadChrono(activiteEnCours.id);
    }
  }
}
